import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, proyeccionesAPI, animalesAPI, pesajesAPI, serviciosAPI, paricionesAPI, frameScoreAPI, alertasAPI, configInvernadaAPI, reportesAPI2, emailAlertasAPI } from '../services/api';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { cacheAnimales, cachePesajes, cacheServicios, cachePariciones, getCacheStats } from '../services/offlineDB';

const fmt = (v) => (v === null || v === undefined ? '—' : typeof v === 'number' ? v.toLocaleString('es-AR') : v);

const PRIO_STYLES = {
  critica: 'bg-red-50 border-red-400 text-red-900',
  alta: 'bg-amber-50 border-amber-400 text-amber-900',
  media: 'bg-blue-50 border-blue-400 text-blue-800',
  info: 'bg-gray-50 border-gray-300 text-gray-700',
};
const PRIO_BADGE = {
  critica: 'bg-red-600 text-white',
  alta: 'bg-amber-500 text-white',
  media: 'bg-blue-500 text-white',
  info: 'bg-gray-400 text-white',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [frameResumen, setFrameResumen] = useState(null);
  const [alertasData, setAlertasData] = useState(null);
  const [invernadaData, setInvernadaData] = useState(null);
  const [metricasAdv, setMetricasAdv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('productor');

  const [downloading, setDownloading] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [downloadMsg, setDownloadMsg] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [invConfig, setInvConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    if (user?.establecimiento_id) loadData();
    loadCacheInfo();
    // Show onboarding for first-time users
    const done = localStorage.getItem('tkx_onboarding_done');
    if (!done) setShowOnboarding(true);
  }, [user]);

  const loadCacheInfo = async () => {
    try { setCacheInfo(await getCacheStats()); } catch {}
  };

  const loadInvConfig = async () => {
    if (!user?.establecimiento_id) return;
    try {
      const res = await configInvernadaAPI.get(user.establecimiento_id);
      setInvConfig(res.data);
    } catch {}
  };

  const saveInvConfig = async (newConfig) => {
    if (!user?.establecimiento_id) return;
    setSavingConfig(true);
    try {
      const res = await configInvernadaAPI.update(user.establecimiento_id, newConfig);
      setInvConfig(res.data);
      // Reload invernada data with new config
      const invRes = await alertasAPI.invernada(user.establecimiento_id);
      if (invRes.status === 200) setInvernadaData(invRes.data);
    } catch {}
    setSavingConfig(false);
  };

  const handlePrepareCampo = async () => {
    if (!user?.establecimiento_id || downloading) return;
    setDownloading(true); setDownloadMsg('');
    try {
      const estId = user.establecimiento_id;
      const [aRes, pRes, sRes, parRes] = await Promise.allSettled([
        animalesAPI.syncAll(estId),
        pesajesAPI.getAll(estId, { page: 1, page_size: 5000 }),
        serviciosAPI.getAll(estId, { page: 1, page_size: 5000 }),
        paricionesAPI.getAll(estId, { page: 1, page_size: 5000 }),
      ]);
      let counts = {};
      if (aRes.status === 'fulfilled') { await cacheAnimales(aRes.value.data.data); counts.animales = aRes.value.data.total; }
      if (pRes.status === 'fulfilled') { await cachePesajes(pRes.value.data.data); counts.pesajes = pRes.value.data.total; }
      if (sRes.status === 'fulfilled') { await cacheServicios(sRes.value.data.data); counts.servicios = sRes.value.data.total; }
      if (parRes.status === 'fulfilled') { await cachePariciones(parRes.value.data.data); counts.pariciones = parRes.value.data.total; }
      setDownloadMsg(`Modo Campo listo: ${counts.animales || 0} animales, ${counts.pesajes || 0} pesajes, ${counts.servicios || 0} servicios, ${counts.pariciones || 0} pariciones — ${new Date().toLocaleString('es-AR')}`);
      loadCacheInfo();
    } catch { setDownloadMsg('Error al descargar. Intenta de nuevo.'); }
    setDownloading(false);
  };

  const handleDescargarReporteIA = async () => {
    setGeneratingPDF(true);
    try {
      const res = await reportesAPI2.pdfIA(user.establecimiento_id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Reporte_IA_${new Date().toISOString().slice(0,10)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al generar el reporte PDF con IA'); }
    setGeneratingPDF(false);
  };

  const handleEnviarAlertas = async () => {
    if (!emailAddr) return;
    setEmailSending(true);
    setEmailMsg('');
    try {
      const res = await emailAlertasAPI.enviarCriticas(user.establecimiento_id, emailAddr);
      setEmailMsg(res.data.message || 'Enviado');
      setTimeout(() => { setEmailModal(false); setEmailMsg(''); }, 3000);
    } catch (e) { setEmailMsg('Error al enviar: ' + (e.response?.data?.detail || e.message)); }
    setEmailSending(false);
  };

  const loadData = async () => {
    setLoading(true);
    const estId = user.establecimiento_id;
    // Fase 1: Solo KPIs + alertas (pestaña principal "Productor")
    const [kpiRes, alertRes] = await Promise.allSettled([
      dashboardAPI.getKPIs(estId),
      alertasAPI.productivas(estId),
    ]);
    if (kpiRes.status === 'fulfilled') setKpis(kpiRes.value.data);
    if (alertRes.status === 'fulfilled') setAlertasData(alertRes.value.data);
    setLoading(false);

    // Fase 2: Datos secundarios en background (no bloquean UI)
    const [frameRes, invRes, metRes, cfgRes] = await Promise.allSettled([
      frameScoreAPI.dashboardResumen(estId),
      alertasAPI.invernada(estId),
      alertasAPI.metricasAvanzadas(estId),
      configInvernadaAPI.get(estId),
    ]);
    if (frameRes.status === 'fulfilled') setFrameResumen(frameRes.value.data);
    if (invRes.status === 'fulfilled') setInvernadaData(invRes.value.data);
    if (metRes.status === 'fulfilled') setMetricasAdv(metRes.value.data);
    if (cfgRes.status === 'fulfilled') setInvConfig(cfgRes.value.data);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'productor', label: 'Informe Productor' },
    { id: 'alertas', label: `Alertas (${alertasData?.resumen?.total_alertas || 0})` },
    { id: 'invernada', label: 'Invernada & Costos' },
    { id: 'cria', label: 'Cria & Eficiencia' },
  ];

  const al = alertasData?.resumen || {};
  const inv = invernadaData?.resumen || {};
  const fr = frameResumen?.frame_score || {};
  const ef = frameResumen?.eficiencia_cria || {};
  const met = metricasAdv || {};

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Centro de Decision</h2>
          <p className="text-sm text-gray-500">{user?.establecimiento_nombre}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleDescargarReporteIA} disabled={generatingPDF}
            className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50 transition" data-testid="reporte-ia-btn">
            {generatingPDF ? 'Generando...' : 'Reporte PDF con IA'}
          </button>
          <button onClick={() => { setEmailModal(true); setEmailMsg(''); }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition" data-testid="enviar-alertas-btn">
            Enviar Alertas por Email
          </button>
          <button onClick={handlePrepareCampo} disabled={downloading}
            className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-semibold hover:bg-green-800 disabled:opacity-50 transition" data-testid="preparar-modo-campo">
            {downloading ? 'Descargando...' : 'Preparar Modo Campo'}
          </button>
        </div>
      </div>
      {downloadMsg && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{downloadMsg}</div>}
      <EmailAlertModal show={emailModal} onClose={() => setEmailModal(false)} email={emailAddr} setEmail={setEmailAddr} onSend={handleEnviarAlertas} sending={emailSending} msg={emailMsg} />
      {showOnboarding && <OnboardingTutorial onComplete={() => setShowOnboarding(false)} />}
      {cacheInfo && cacheInfo.total > 0 && !downloadMsg && (
        <div className="p-2 bg-gray-50 rounded-lg text-xs text-gray-500">Cache: {cacheInfo.total} registros locales</div>
      )}

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto" data-testid="dashboard-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition whitespace-nowrap ${activeTab === t.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== TAB 1: INFORME PRODUCTOR ===== */}
      {activeTab === 'productor' && (
        <div className="space-y-6" data-testid="tab-productor-content">
          {/* Alertas urgentes arriba */}
          {al.criticas > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4" data-testid="alertas-urgentes">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <h3 className="font-bold text-red-900">Atencion: {al.criticas} alerta{al.criticas > 1 ? 's' : ''} critica{al.criticas > 1 ? 's' : ''}</h3>
              </div>
              {alertasData?.alertas?.filter(a => a.prioridad === 'critica').slice(0, 3).map((a, i) => (
                <p key={i} className="text-sm text-red-800 ml-5">- {a.mensaje}</p>
              ))}
              <button onClick={() => setActiveTab('alertas')} className="mt-2 ml-5 text-sm text-red-700 font-semibold underline">Ver todas las alertas</button>
            </div>
          )}

          {/* KPIs principales - lo que el productor quiere ver */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Animales" value={kpis?.total_animales || 0} icon="M" border="border-gray-400" />
            <KpiCard label="Tasa Prenez" value={kpis?.tasa_preñez ? `${kpis.tasa_preñez}%` : '—'} icon="R" border="border-pink-400" sub={`${kpis?.vacas_preñadas || 0} prenadas`}
              emptyHint="Registrá tu primer servicio para ver este dato →" onEmptyClick={() => navigate('/reproduccion')} />
            <KpiCard label="EP Promedio" value={ef.eficiencia_promedio ? `${ef.eficiencia_promedio}%` : '—'} icon="E" border="border-green-400" sub={`${ef.total_vacas_evaluadas || 0} vacas`}
              emptyHint="Cargá pariciones y pesos de destete para calcular EP →" onEmptyClick={() => navigate('/reproduccion')} />
            <KpiCard label="Alertas" value={al.total_alertas || 0} icon="!" border={al.criticas > 0 ? 'border-red-500' : 'border-amber-400'} sub={al.criticas > 0 ? `${al.criticas} criticas` : 'Sin criticas'} />
          </div>

          {/* Frame Score + Invernada resumen */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Frame Score */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />Frame Score (12-18 meses)
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{fr.en_evaluacion_12_18m || 0}</p>
                  <p className="text-xs text-gray-500">En evaluacion</p>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <p className="text-xl font-bold text-indigo-700">{fr.frame_promedio || '—'}</p>
                  <p className="text-xs text-gray-500">Frame Prom.</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gradient-to-r from-green-50 to-amber-50">
                  <div className="flex justify-center gap-2 text-sm font-bold">
                    <span className="text-green-700">{fr.destinos?.reproductor || 0}R</span>
                    <span className="text-amber-700">{fr.destinos?.invernada || 0}I</span>
                    <span className="text-red-700">{fr.destinos?.faena || 0}F</span>
                  </div>
                  <p className="text-xs text-gray-500">Destino</p>
                </div>
              </div>
              <a href="/frame-score" className="text-sm text-green-700 font-semibold hover:underline" data-testid="link-frame-score">Ver evaluacion completa</a>
            </div>

            {/* Invernada resumen */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />Invernada — Decision Suplementacion
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-700">{(inv.suplementar_urgente || 0) + (inv.suplementar || 0)}</p>
                  <p className="text-xs text-gray-500">Suplementar</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-700">{inv.evaluar || 0}</p>
                  <p className="text-xs text-gray-500">Evaluar</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-700">{inv.mantener || 0}</p>
                  <p className="text-xs text-gray-500">OK sin sup.</p>
                </div>
              </div>
              {inv.costo_suplementacion_estimado_usd > 0 && (
                <p className="text-sm text-gray-600">Costo suplementacion estimado: <strong>USD {fmt(inv.costo_suplementacion_estimado_usd)}</strong></p>
              )}
              <button onClick={() => setActiveTab('invernada')} className="mt-2 text-sm text-amber-700 font-semibold hover:underline" data-testid="link-invernada">Ver analisis completo</button>
            </div>
          </div>

          {/* Eficiencia de Cria - Top/Bottom */}
          {ef.top_3?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />Eficiencia de Cria — Quien da mas plata
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">Mejores vacas</h4>
                  {ef.top_3.map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-green-50 rounded-lg mb-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">{i+1}</span>
                        <span className="font-semibold">VID {v.VID}</span>
                        <span className="text-gray-500">{Number(v.peso_madre).toFixed(2)}kg madre → {Number(v.peso_destete).toFixed(2)}kg cria</span>
                      </div>
                      <span className="font-bold text-green-700">{v.eficiencia}%</span>
                    </div>
                  ))}
                </div>
                {ef.bottom_3?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-700 mb-2">Candidatas descarte</h4>
                    {ef.bottom_3.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg mb-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold">!</span>
                          <span className="font-semibold">VID {v.VID}</span>
                          <span className="text-gray-500">{Number(v.peso_madre).toFixed(2)}kg → {Number(v.peso_destete).toFixed(2)}kg</span>
                        </div>
                        <span className="font-bold text-red-700">{v.eficiencia}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metricas Avanzadas */}
          {met.tasa_procreo && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Metricas Productivas (PDF)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard label="Tasa Procreo" value={`${met.tasa_procreo.valor}%`} desc={met.tasa_procreo.desc} />
                <MetricCard label="Peso Destete Prom." value={`${met.peso_destete_promedio?.valor || 0} kg`} />
                <MetricCard label="Productividad/UA" value={`${met.productividad_ua?.valor || 0} kg/UA`} desc={met.productividad_ua?.desc} />
                <MetricCard label="Merma Terneros" value={`${met.merma_terneros?.valor || 0}%`} desc={`${met.merma_terneros?.perdidos || 0} de ${met.merma_terneros?.nacidos || 0}`} alert={met.merma_terneros?.valor > 10} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== TAB 2: ALERTAS ===== */}
      {activeTab === 'alertas' && (
        <div className="space-y-4" data-testid="tab-alertas-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-2xl font-bold text-red-700">{al.criticas || 0}</p>
              <p className="text-xs text-red-600">Criticas</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-2xl font-bold text-amber-700">{al.altas || 0}</p>
              <p className="text-xs text-amber-600">Altas</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-2xl font-bold text-blue-700">{al.medias || 0}</p>
              <p className="text-xs text-blue-600">Medias</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-2xl font-bold text-gray-700">{al.total_alertas || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>

          {alertasData?.alertas?.map((a, i) => (
            <div key={i} className={`rounded-xl border-l-4 p-4 ${PRIO_STYLES[a.prioridad] || PRIO_STYLES.info}`} data-testid={`alerta-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${PRIO_BADGE[a.prioridad]}`}>{a.prioridad}</span>
                    <span className="text-xs text-gray-500 capitalize">{a.tipo}</span>
                  </div>
                  <p className="text-sm font-semibold">{a.mensaje}</p>
                  <p className="text-xs text-gray-600 mt-1">{a.criterio}</p>
                  <p className="text-xs font-medium mt-1 text-gray-800">{a.accion}</p>
                </div>
              </div>
            </div>
          ))}

          {(!alertasData?.alertas || alertasData.alertas.length === 0) && (
            <div className="text-center py-12 text-gray-400">Sin alertas activas. El rodeo esta en orden.</div>
          )}
        </div>
      )}

      {/* ===== TAB 3: INVERNADA & COSTOS ===== */}
      {activeTab === 'invernada' && (
        <div className="space-y-5" data-testid="tab-invernada-content">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Analisis de Invernada — Costo vs Beneficio de Suplementacion</h3>
                <p className="text-sm text-amber-700">
                  Analisis 100% automatico basado en los datos de tu rodeo. Los precios y costos se configuran una sola vez.
                </p>
              </div>
              <button onClick={() => setShowConfig(!showConfig)}
                className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition shrink-0"
                data-testid="btn-config-invernada">
                {showConfig ? 'Cerrar Config' : 'Configurar Precios'}
              </button>
            </div>
          </div>

          {/* Config Panel */}
          {showConfig && invConfig && (
            <InvernadaConfigPanel config={invConfig} onSave={saveInvConfig} saving={savingConfig} />
          )}

          {/* Active config display */}
          {invernadaData?.config && (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="px-2.5 py-1 bg-gray-100 rounded-full text-gray-600">
                Sistema: <strong>{invernadaData.config.sistema?.replace('_',' ')}</strong>
              </span>
              <span className="px-2.5 py-1 bg-gray-100 rounded-full text-gray-600">
                Suplemento: <strong>USD {invernadaData.config.precio_suplemento_kg}/kg</strong>
              </span>
              <span className="px-2.5 py-1 bg-gray-100 rounded-full text-gray-600">
                Novillo: <strong>USD {invernadaData.config.precio_kg_novillo}/kg</strong>
              </span>
              <span className="px-2.5 py-1 bg-gray-100 rounded-full text-gray-600">
                Vaquilla: <strong>USD {invernadaData.config.precio_kg_vaquilla}/kg</strong>
              </span>
              {!invernadaData.config.personalizado && (
                <span className="px-2.5 py-1 bg-amber-100 rounded-full text-amber-700 font-semibold">
                  Usando precios por defecto
                </span>
              )}
            </div>
          )}

          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SCard label="Total Animales" value={inv.total_animales || 0} border="border-gray-300" />
            <SCard label="Suplementar" value={(inv.suplementar_urgente || 0) + (inv.suplementar || 0)} border="border-red-400" color="text-red-700" />
            <SCard label="Evaluar" value={inv.evaluar || 0} border="border-amber-400" color="text-amber-700" />
            <SCard label="OK sin sup." value={inv.mantener || 0} border="border-green-400" color="text-green-700" />
            <SCard label="Sin datos GMD" value={Math.max(0, (inv.total_animales || 0) - (inv.suplementar_urgente || 0) - (inv.suplementar || 0) - (inv.evaluar || 0) - (inv.mantener || 0))} border="border-gray-300" color="text-gray-500" />
          </div>

          {(inv.costo_suplementacion_estimado_usd > 0 || inv.ingreso_extra_estimado_usd > 0) && (
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="invernada-resumen-economico">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Animales rentables</p>
                  <p className="text-lg font-bold text-gray-800">{inv.rentables || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Costo suplementacion</p>
                  <p className="text-lg font-bold text-red-700">USD {fmt(inv.costo_suplementacion_estimado_usd)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Ingreso extra estimado</p>
                  <p className="text-lg font-bold text-green-700">USD {fmt(inv.ingreso_extra_estimado_usd)}</p>
                </div>
              </div>
              {inv.beneficio_neto_estimado_usd > 0 && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold" data-testid="invernada-beneficio-neto">
                  Beneficio neto: +USD {fmt(inv.beneficio_neto_estimado_usd)}
                </span>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500" data-testid="invernada-criterios">
            Criterio (estándar invierno Paraguay): GMD &lt; 0 → recuperación 90 días, proteico 0.5% PV, +0.30 kg/día ·
            GMD 0–0.5 → 60–90 días al 1% PV · GMD 0.5–0.7 → evaluar 60 días · GMD ≥ 0.7 → no requiere. Conversión 6 kg suplemento : 1 kg carne.
          </p>

          {/* Tabla de animales */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="invernada-table">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['VID', 'Sexo', 'Edad', 'Peso', 'GMD', 'GMD c/Sup', 'Frame', 'Decision', 'Sup. $/dia', 'Dias', 'Sup. Total', 'Sin Sup. $', 'Con Sup. $', 'Ingreso Extra', 'Rentable'].map(h => (
                      <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invernadaData?.analisis?.map((a, i) => {
                    const decColor = {suplementar_urgente: 'bg-red-100 text-red-800', suplementar: 'bg-amber-100 text-amber-800', evaluar: 'bg-blue-100 text-blue-800', mantener: 'bg-green-100 text-green-800', sin_datos: 'bg-gray-100 text-gray-600'};
                    const decLabel = {suplementar_urgente: 'URGENTE', suplementar: 'Suplementar', evaluar: 'Evaluar', mantener: 'OK sin sup.', sin_datos: 'Sin GMD'};
                    return (
                      <tr key={a.animal_id} title={a.razon} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/50' : ''}`}>
                        <td className="py-2.5 px-3 font-semibold">{a.VID}</td>
                        <td className="py-2.5 px-3 text-xs capitalize">{a.sexo === 'macho' ? 'M' : 'H'}</td>
                        <td className="py-2.5 px-3">{a.edad_meses}m</td>
                        <td className="py-2.5 px-3">{a.peso_actual?.toFixed(0)} kg</td>
                        <td className="py-2.5 px-3">{a.gmd_actual != null ? `${a.gmd_actual.toFixed(2)}` : '—'}</td>
                        <td className="py-2.5 px-3 text-emerald-700">{a.gmd_proyectada != null ? a.gmd_proyectada.toFixed(2) : '—'}</td>
                        <td className="py-2.5 px-3">{a.frame?.toFixed(1)}</td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${decColor[a.decision_suplementacion] || 'bg-gray-100'}`}>
                            {decLabel[a.decision_suplementacion] || a.decision_suplementacion}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">{a.suplemento.dias > 0 ? `$${a.suplemento.costo_diario_usd}` : '—'}</td>
                        <td className="py-2.5 px-3">{a.suplemento.dias > 0 ? `${a.suplemento.dias}d` : '—'}</td>
                        <td className="py-2.5 px-3 font-semibold">{a.suplemento.dias > 0 ? `$${a.suplemento.costo_total_usd}` : '—'}</td>
                        <td className="py-2.5 px-3">{a.suplemento.dias > 0 ? `$${a.sin_suplemento.valor_usd}` : '—'}</td>
                        <td className="py-2.5 px-3">{a.suplemento.dias > 0 ? `$${a.con_suplemento.valor_usd}` : '—'}</td>
                        <td className="py-2.5 px-3">{a.suplemento.dias > 0 ? `$${a.ingreso_extra_usd}` : '—'}</td>
                        <td className="py-2.5 px-3">
                          {a.suplemento.dias === 0
                            ? <span className="text-gray-400">N/A</span>
                            : a.suplementacion_rentable
                              ? <span className="text-green-700 font-bold">Si +${a.beneficio_neto_usd}</span>
                              : <span className="text-red-600 font-semibold">No ({a.beneficio_neto_usd < 0 ? '-' : ''}${Math.abs(a.beneficio_neto_usd)})</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {(!invernadaData?.analisis || invernadaData.analisis.length === 0) && (
                    <tr><td colSpan={15} className="text-center py-8 text-gray-400">Sin animales en invernada</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== TAB 4: CRIA & EFICIENCIA ===== */}
      {activeTab === 'cria' && (
        <div className="space-y-5" data-testid="tab-cria-content">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
            <h3 className="font-bold text-green-900 mb-2">Eficiencia de Cria — Peso Ternero / Peso Madre x 100</h3>
            <p className="text-sm text-green-700">
              Vaca 400 kg → ternero 200 kg = <strong>50%</strong> (excelente, menor costo). Vaca 700 kg → ternero 200 kg = <strong>28.5%</strong> (mala, descarte).
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SCard label="Vacas Evaluadas" value={ef.total_vacas_evaluadas || 0} border="border-gray-300" />
            <SCard label="EP Promedio" value={ef.eficiencia_promedio ? `${ef.eficiencia_promedio}%` : '—'} border="border-green-400" color="text-green-700" />
            <SCard label="Tasa Procreo" value={met.tasa_procreo ? `${met.tasa_procreo.valor}%` : '—'} border="border-blue-400" color="text-blue-700" />
            <SCard label="Productividad/UA" value={met.productividad_ua ? `${met.productividad_ua.valor}` : '—'} border="border-indigo-400" color="text-indigo-700" />
          </div>

          {/* Pariciones y destete */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h4 className="font-bold text-gray-900 mb-3">Datos Reproductivos</h4>
              <div className="space-y-2.5">
                <MetricRow label="Tasa de prenez" value={kpis?.tasa_preñez ? `${kpis.tasa_preñez}%` : '—'} />
                <MetricRow label="Pariciones este ano" value={kpis?.pariciones_año || 0} />
                <MetricRow label="Destetes este ano" value={kpis?.destetes_año || 0} />
                <MetricRow label="EP promedio rodeo" value={kpis?.ep_promedio ? `${kpis.ep_promedio}%` : '—'} />
                <MetricRow label="Tasa procreo" value={met.tasa_procreo ? `${met.tasa_procreo.valor}%` : '—'} />
                <MetricRow label="Merma terneros" value={met.merma_terneros ? `${met.merma_terneros.valor}%` : '—'} alert={met.merma_terneros?.valor > 10} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5">
              <h4 className="font-bold text-gray-900 mb-3">Productividad del Hato</h4>
              <div className="space-y-2.5">
                <MetricRow label="Vacas activas" value={met.vacas_activas || 0} />
                <MetricRow label="UA totales" value={met.ua_totales || 0} />
                <MetricRow label="Kg destetados total" value={met.kg_destetados_total ? `${met.kg_destetados_total.valor} kg` : '—'} />
                <MetricRow label="Peso destete promedio" value={met.peso_destete_promedio ? `${met.peso_destete_promedio.valor} kg` : '—'} />
                <MetricRow label="Prod. por vaca/ano" value={met.productividad_vaca_año ? `${met.productividad_vaca_año.valor} kg` : '—'} />
                <MetricRow label="Prod. por UA" value={met.productividad_ua ? `${met.productividad_ua.valor} kg/UA` : '—'} />
              </div>
            </div>
          </div>

          {/* Top/Bottom vacas */}
          {ef.top_3?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h4 className="font-bold text-gray-900 mb-3">Ranking de Vacas por Eficiencia</h4>
              <div className="space-y-2">
                {ef.top_3.map((v, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg text-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">{i+1}</span>
                      <span className="font-semibold">VID {v.VID}</span>
                      <span className="text-gray-500">{Number(v.peso_madre).toFixed(2)} kg → {Number(v.peso_destete).toFixed(2)} kg ({v.terneros} terneros)</span>
                    </div>
                    <span className="font-bold text-green-700 text-lg">{v.eficiencia}%</span>
                  </div>
                ))}
              </div>
              {ef.bottom_3?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-semibold text-red-700">Candidatas descarte (EP &lt; umbral)</h4>
                  {ef.bottom_3.map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg text-sm">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold">!</span>
                        <span className="font-semibold">VID {v.VID}</span>
                        <span className="text-gray-500">{Number(v.peso_madre).toFixed(2)} kg → {Number(v.peso_destete).toFixed(2)} kg</span>
                      </div>
                      <span className="font-bold text-red-700 text-lg">{v.eficiencia}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="text-center">
            <a href="/frame-score" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition shadow-sm" data-testid="link-frame-detalle">
              Ver evaluacion completa de Frame Score & Eficiencia
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Sub-components ---
const KpiCard = ({ label, value, icon, border, sub, emptyHint, onEmptyClick }) => {
  const isEmpty = value === '—';
  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${border} hover:shadow-md transition`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {isEmpty && emptyHint ? (
        <button onClick={onEmptyClick} className="text-xs text-green-700 font-medium mt-0.5 underline underline-offset-2 hover:text-green-900 text-left" data-testid={`kpi-hint-${label.toLowerCase().replace(/\s+/g,'-')}`}>
          {emptyHint}
        </button>
      ) : (
        sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
      )}
    </div>
  );
};

const SCard = ({ label, value, border, color = 'text-gray-900' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${border}`}>
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);

const MetricCard = ({ label, value, desc, alert }) => (
  <div className={`rounded-xl p-4 ${alert ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className={`text-xl font-bold mt-1 ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
    {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
  </div>
);

const MetricRow = ({ label, value, alert }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-sm font-semibold ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</span>
  </div>
);

const InvernadaConfigPanel = ({ config, onSave, saving }) => {
  const [form, setForm] = useState({
    precio_suplemento_kg: config?.precio_suplemento_kg ?? 0.35,
    precio_kg_novillo: config?.precio_kg_novillo ?? 2.2,
    precio_kg_vaquilla: config?.precio_kg_vaquilla ?? 2.0,
    sistema_engorde: config?.sistema_engorde ?? 'semi_intensivo',
    costo_pastoril_dia: config?.costo_pastoril_dia ?? 0.8,
    costo_semi_intensivo_dia: config?.costo_semi_intensivo_dia ?? 1.5,
    costo_feedlot_dia: config?.costo_feedlot_dia ?? 2.5,
    peso_objetivo_novillo: config?.peso_objetivo_novillo ?? '',
    peso_objetivo_vaquilla: config?.peso_objetivo_vaquilla ?? '',
    incluir_hembras: config?.incluir_hembras ?? true,
    duracion_servicio_dias: config?.duracion_servicio_dias ?? 45,
    etapa_programa: config?.etapa_programa ?? 'etapa_2',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    const data = { ...form };
    if (!data.peso_objetivo_novillo) data.peso_objetivo_novillo = null;
    else data.peso_objetivo_novillo = parseFloat(data.peso_objetivo_novillo);
    if (!data.peso_objetivo_vaquilla) data.peso_objetivo_vaquilla = null;
    else data.peso_objetivo_vaquilla = parseFloat(data.peso_objetivo_vaquilla);
    onSave(data);
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5 space-y-4" data-testid="invernada-config-panel">
      <h4 className="font-bold text-gray-900 text-sm">Configuracion de Precios y Costos</h4>
      <p className="text-xs text-gray-500">Estos valores se guardan para tu establecimiento. El analisis se recalcula automaticamente.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Precio suplemento (USD/kg)</label>
          <input type="number" step="0.01" value={form.precio_suplemento_kg} onChange={e => set('precio_suplemento_kg', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-precio-suplemento" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Precio venta novillo (USD/kg vivo)</label>
          <input type="number" step="0.01" value={form.precio_kg_novillo} onChange={e => set('precio_kg_novillo', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-precio-novillo" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Precio venta vaquilla (USD/kg vivo)</label>
          <input type="number" step="0.01" value={form.precio_kg_vaquilla} onChange={e => set('precio_kg_vaquilla', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-precio-vaquilla" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sistema de engorde preferido</label>
          <select value={form.sistema_engorde} onChange={e => set('sistema_engorde', e.target.value)} className={inputCls} data-testid="cfg-sistema-engorde">
            <option value="pastoril">Pastoril</option>
            <option value="semi_intensivo">Semi-Intensivo</option>
            <option value="intensivo_feedlot">Feedlot Intensivo</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Costo diario pastoril (USD)</label>
          <input type="number" step="0.01" value={form.costo_pastoril_dia} onChange={e => set('costo_pastoril_dia', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-costo-pastoril" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Costo diario semi-intensivo (USD)</label>
          <input type="number" step="0.01" value={form.costo_semi_intensivo_dia} onChange={e => set('costo_semi_intensivo_dia', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-costo-semi" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Costo diario feedlot (USD)</label>
          <input type="number" step="0.01" value={form.costo_feedlot_dia} onChange={e => set('costo_feedlot_dia', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="cfg-costo-feedlot" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Peso objetivo novillo (kg, vacio = usa Frame)</label>
          <input type="number" step="1" value={form.peso_objetivo_novillo} onChange={e => set('peso_objetivo_novillo', e.target.value)} className={inputCls} placeholder="Ej: 500" data-testid="cfg-peso-novillo" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Peso objetivo vaquilla (kg, vacio = usa Frame)</label>
          <input type="number" step="1" value={form.peso_objetivo_vaquilla} onChange={e => set('peso_objetivo_vaquilla', e.target.value)} className={inputCls} placeholder="Ej: 430" data-testid="cfg-peso-vaquilla" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Duracion servicio (dias)</label>
          <input type="number" step="1" min="15" max="90" value={form.duracion_servicio_dias} onChange={e => set('duracion_servicio_dias', parseInt(e.target.value) || 45)} className={inputCls} data-testid="cfg-duracion-servicio" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Etapa del programa</label>
          <select value={form.etapa_programa} onChange={e => set('etapa_programa', e.target.value)} className={inputCls} data-testid="cfg-etapa-programa">
            <option value="etapa_1">Etapa 1 (45 dias servicio)</option>
            <option value="etapa_2">Etapa 2 (45 dias + seleccion)</option>
            <option value="etapa_3">Etapa 3 (30 dias + rodeo cerrado)</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.incluir_hembras} onChange={e => set('incluir_hembras', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" data-testid="cfg-incluir-hembras" />
          <span className="text-sm text-gray-700">Incluir hembras en analisis de invernada</span>
        </label>
      </div>

      <div className="flex justify-end pt-2">
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition"
          data-testid="btn-guardar-config">
          {saving ? 'Guardando...' : 'Guardar y Recalcular'}
        </button>
      </div>
    </div>
  );
};

// Modal de email para alertas
const EmailAlertModal = ({ show, onClose, email, setEmail, onSend, sending, msg }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Enviar Alertas Criticas por Email</h3>
        <p className="text-sm text-gray-500 mb-4">Se enviaran las alertas criticas y altas del rodeo al email indicado.</p>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="ejemplo@correo.com"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-3"
          data-testid="email-input" />
        {msg && <p className={`text-sm mb-3 ${msg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button onClick={onSend} disabled={sending || !email}
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
            data-testid="send-email-btn">
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
